/**
 * Edit a customer's identity: photograph, name, email, date of birth, alternate number.
 *
 * Validation runs as you type using the same pure module the save path uses, and the
 * reason a save is blocked is always on screen — a greyed-out button that explains
 * nothing reads as broken.
 *
 * The mobile number is NOT editable here. It is the key the whole customer tree, the
 * wallets and every order are filed under, so changing it is a merge, not an edit.
 */
import React, { useMemo, useRef, useState } from 'react';
import { X, Camera, Trash2, AlertTriangle, Cake, Loader2 } from 'lucide-react';
import { CustomerService } from '../services/CustomerService';
import { validateProfile, validatePhoto, ageFrom, daysToBirthday } from '../logic/customerProfileFields';

const Field = ({ label, hint, error, children }) => (
    <div>
        <label className="text-[12px] font-semibold text-slate-600">{label}</label>
        {children}
        {error
            ? <p className="text-[11px] text-red-600 mt-1">{error}</p>
            : hint ? <p className="text-[11px] text-slate-400 mt-1">{hint}</p> : null}
    </div>
);

const EditCustomerModal = ({ customer, mobile, actor = 'staff', onClose, onSaved }) => {
    const [form, setForm] = useState({
        name: customer?.name || customer?.customerName || '',
        email: customer?.email || '',
        dateOfBirth: customer?.dateOfBirth || '',
        secondaryMobile: customer?.secondaryMobile || '',
        gender: customer?.gender || '',
        notes: customer?.notes || '',
    });
    const [photo, setPhoto] = useState({ url: customer?.photoUrl || '', path: customer?.photoPath || '', file: null, preview: '' });
    const [busy, setBusy] = useState('');
    const [err, setErr] = useState('');
    const fileRef = useRef(null);

    const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErr(''); };

    const check = useMemo(
        () => validateProfile({ ...form, primaryMobile: mobile }),
        [form, mobile],
    );

    const age = ageFrom(form.dateOfBirth);
    const toBirthday = daysToBirthday(form.dateOfBirth);

    const pickPhoto = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const v = validatePhoto(file);
        if (!v.ok) { setErr(v.error); return; }
        setErr('');
        setPhoto(p => ({ ...p, file, preview: URL.createObjectURL(file) }));
    };

    const dropPhoto = async () => {
        if (photo.file) { setPhoto(p => ({ ...p, file: null, preview: '' })); return; }
        if (!photo.url) return;
        if (!window.confirm('Remove this customer’s photograph?')) return;
        setBusy('photo'); setErr('');
        try {
            await CustomerService.removePhoto(mobile, { actor, previousPath: photo.path });
            setPhoto({ url: '', path: '', file: null, preview: '' });
        } catch (e) { setErr(e.message); }
        finally { setBusy(''); }
    };

    const save = async () => {
        if (!check.ok) { setErr(check.error); return; }
        setBusy('save'); setErr('');
        try {
            // Only what changed, so an untouched field is never rewritten and the audit
            // line stays honest.
            const before = {
                name: customer?.name || '', email: customer?.email || '',
                dateOfBirth: customer?.dateOfBirth || '', secondaryMobile: customer?.secondaryMobile || '',
                gender: customer?.gender || '', notes: customer?.notes || '',
            };
            const changes = {};
            const patch = {};
            for (const [k, v] of Object.entries(check.values)) {
                if (String(before[k] ?? '') !== String(v ?? '')) { patch[k] = v; changes[k] = { from: before[k] || '', to: v }; }
            }

            if (photo.file) {
                setBusy('photo');
                await CustomerService.uploadPhoto(mobile, photo.file, { actor, previousPath: photo.path });
                setBusy('save');
            }
            if (Object.keys(patch).length) {
                await CustomerService.updateProfile(mobile, patch, { actor, changes });
            }
            onSaved?.();
            onClose?.();
        } catch (e) { setErr(e.message || 'Could not save the changes.'); }
        finally { setBusy(''); }
    };

    const shown = photo.preview || photo.url;
    const input = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-[13px] mt-1';

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="px-4 py-3 border-b border-slate-100 flex items-center">
                    <h3 className="font-bold text-slate-800">Edit customer</h3>
                    <button type="button" onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700"><X size={18} /></button>
                </div>

                <div className="p-4 space-y-3">
                    {/* Photograph */}
                    <div className="flex items-center gap-3">
                        <div className="w-20 h-20 rounded-full bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                            {shown
                                ? <img src={shown} alt="" className="w-full h-full object-cover" />
                                : <span className="text-2xl font-bold text-slate-400">{(form.name || '?').trim().charAt(0).toUpperCase()}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex gap-2">
                                <button type="button" onClick={() => fileRef.current?.click()} disabled={!!busy}
                                    className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold text-slate-600 disabled:opacity-50">
                                    {busy === 'photo' ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
                                    {shown ? 'Change photo' : 'Add photo'}
                                </button>
                                {shown && (
                                    <button type="button" onClick={dropPhoto} disabled={!!busy}
                                        className="flex items-center gap-1.5 text-slate-400 hover:text-rose-600 rounded-lg px-2 py-1.5 text-[12px] font-semibold disabled:opacity-50">
                                        <Trash2 size={13} /> Remove
                                    </button>
                                )}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-1">JPG, PNG or WebP, under 5 MB. Saved when you save the form.</p>
                            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pickPhoto} className="hidden" />
                        </div>
                    </div>

                    <Field label="Name" error={check.field === 'name' ? check.error : ''}>
                        <input value={form.name} onChange={e => set('name', e.target.value)} className={input} placeholder="Full name" />
                    </Field>

                    <Field
                        label="Email"
                        hint="Used for the invoice and order updates. Leave blank if they have none."
                        error={check.field === 'email' ? check.error : ''}
                    >
                        <input type="email" value={form.email} onChange={e => set('email', e.target.value)} className={input} placeholder="name@example.com" />
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                        <Field
                            label="Date of birth"
                            hint={age != null ? `${age} years old${toBirthday === 0 ? ' — birthday today' : toBirthday != null && toBirthday <= 30 ? ` · birthday in ${toBirthday} days` : ''}` : 'Lets you run birthday offers later.'}
                            error={check.field === 'dateOfBirth' ? check.error : ''}
                        >
                            <input type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} className={input} max={new Date().toISOString().slice(0, 10)} />
                        </Field>
                        <Field label="Gender" hint="Optional.">
                            <select value={form.gender} onChange={e => set('gender', e.target.value)} className={input}>
                                <option value="">Not recorded</option>
                                <option value="female">Female</option>
                                <option value="male">Male</option>
                                <option value="other">Other</option>
                            </select>
                        </Field>
                    </div>

                    <Field
                        label="Alternate mobile"
                        hint="A second number for the courier to try."
                        error={check.field === 'secondaryMobile' ? check.error : ''}
                    >
                        <input value={form.secondaryMobile} onChange={e => set('secondaryMobile', e.target.value)} className={input} placeholder="98765 43210" />
                    </Field>

                    <Field label="Internal note" hint="Only your team sees this.">
                        <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} className={input} />
                    </Field>

                    <p className="text-[11px] text-slate-400 bg-slate-50 rounded-lg px-3 py-2">
                        The mobile number <b>{mobile}</b> cannot be changed here — every order, ticket and wallet is filed under it.
                    </p>

                    {age != null && toBirthday != null && toBirthday <= 7 && (
                        <p className="text-[12px] text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 flex items-center gap-2">
                            <Cake size={14} /> {toBirthday === 0 ? 'Their birthday is today.' : `Their birthday is in ${toBirthday} day${toBirthday === 1 ? '' : 's'}.`}
                        </p>
                    )}

                    {err && <p className="text-[12px] text-red-600 flex items-start gap-1.5"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {err}</p>}
                </div>

                <div className="px-4 py-3 border-t border-slate-100">
                    {!check.ok && <p className="text-[12px] text-amber-800 mb-2 flex items-start gap-1.5"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {check.error}</p>}
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 text-[12.5px] font-semibold">Cancel</button>
                        <button type="button" disabled={!!busy} onClick={save} className="flex-1 px-3 py-2 rounded-lg bg-purple-600 text-white text-[12.5px] font-semibold disabled:opacity-60">
                            {busy ? 'Saving…' : 'Save changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditCustomerModal;
